import { ResponsiveTable } from './components/common/ResponsiveTable';
import React, { useEffect, useState } from 'react';
import { API_CONFIG, getApiUrl, getAuthHeaders } from './config/api';
import { formatDate } from './utils/dateFormat';
import { BolivianDateInput } from './components/common/BolivianDateInput';

export default function PaymentList({ token, patientId }: { token: string, patientId: number }) {
  const [payments, setPayments] = useState<any[]>([]);
  const [form, setForm] = useState({ date: '', amount: '', method: '', notes: '', appointmentId: '' });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchPayments = () => {
    setLoading(true);
    fetch(getApiUrl(API_CONFIG.ENDPOINTS.PATIENT_PAYMENTS, { patientId }), {
      headers: getAuthHeaders(token)
    })
      .then(res => res.json())
      .then(setPayments)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  // Fetching on token/patient change is intentional.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchPayments(); }, [token, patientId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      const res = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.PAYMENTS), {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ ...form, patientId, amount: parseFloat(form.amount), appointmentId: form.appointmentId ? parseInt(form.appointmentId) : undefined })
      });
      if (!res.ok) throw new Error('Failed to create payment');
      setForm({ date: '', amount: '', method: '', notes: '', appointmentId: '' });
      fetchPayments();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleEdit = (p: any) => {
    setEditingId(p.id);
    setForm({ date: p.date?.slice(0, 10), amount: p.amount, method: p.method, notes: p.notes, appointmentId: p.appointmentId || '' });
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      const res = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.PAYMENTS}/${editingId}`), {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ ...form, patientId, amount: parseFloat(form.amount), appointmentId: form.appointmentId ? parseInt(form.appointmentId) : undefined })
      });
      if (!res.ok) throw new Error('Failed to update payment');
      setEditingId(null);
      setForm({ date: '', amount: '', method: '', notes: '', appointmentId: '' });
      fetchPayments();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this payment?')) return;
    setError('');
    try {
      const res = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.PAYMENTS}/${id}`), {
        method: 'DELETE',
        headers: getAuthHeaders(token)
      });
      if (!res.ok) throw new Error('Failed to delete payment');
      fetchPayments();
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div style={{ color: 'red' }}>{error}</div>;

  return (
    <div>
      <h3>Payments</h3>
      <form className="responsive-row" onSubmit={editingId ? handleUpdate : handleSubmit} style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <BolivianDateInput name="date" value={form.date} onChange={date => setForm(current => ({ ...current, date }))} required style={{ padding: '6px', border: '1px solid #334155', borderRadius: 4, background: '#0f172a', color: '#f1f5f9' }} />
        <input name="amount" type="number" step="0.01" placeholder="Amount" value={form.amount} onChange={handleChange} required style={{ padding: '6px', border: '1px solid #334155', borderRadius: 4, background: '#0f172a', color: '#f1f5f9' }} />
        <input name="method" placeholder="Method" value={form.method} onChange={handleChange} required style={{ padding: '6px', border: '1px solid #334155', borderRadius: 4, background: '#0f172a', color: '#f1f5f9' }} />
        <input name="appointmentId" type="number" placeholder="Appointment ID (optional)" value={form.appointmentId} onChange={handleChange} style={{ padding: '6px', border: '1px solid #334155', borderRadius: 4, background: '#0f172a', color: '#f1f5f9' }} />
        <textarea name="notes" placeholder="Notes" value={form.notes} onChange={handleChange} style={{ padding: '6px', border: '1px solid #334155', borderRadius: 4, background: '#0f172a', color: '#f1f5f9', minWidth: 200 }} />
        <button type="submit" style={{ padding: '6px 12px', background: '#1976d2', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>{editingId ? 'Update' : 'Add'} Payment</button>
        {editingId && <button type="button" onClick={() => { setEditingId(null); setForm({ date: '', amount: '', method: '', notes: '', appointmentId: '' }); }} style={{ padding: '6px 12px', background: '#64748b', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Cancel</button>}
      </form>
      <ResponsiveTable><table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: '#1e293b', color: '#f1f5f9' }}>
            <th style={{ padding: 8, textAlign: 'left' }}>Date</th>
            <th style={{ padding: 8, textAlign: 'left' }}>Amount</th>
            <th style={{ padding: 8, textAlign: 'left' }}>Method</th>
            <th style={{ padding: 8, textAlign: 'left' }}>Appointment ID</th>
            <th style={{ padding: 8, textAlign: 'left' }}>Notes</th>
            <th style={{ padding: 8, textAlign: 'left' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {payments.map(p => (
            <tr key={p.id} style={{ borderBottom: '1px solid #334155', color: '#f1f5f9' }}>
              <td style={{ padding: 8 }}>{formatDate(p.date)}</td>
              <td style={{ padding: 8 }}>{p.amount}</td>
              <td style={{ padding: 8 }}>{p.method}</td>
              <td style={{ padding: 8 }}>{p.appointmentId}</td>
              <td style={{ padding: 8 }}>{p.notes}</td>
              <td style={{ padding: 8 }}>
                <button onClick={() => handleEdit(p)} style={{ marginRight: 8, padding: '4px 8px', cursor: 'pointer' }}>Edit</button>
                <button onClick={() => handleDelete(p.id)} style={{ background: '#ef4444', color: 'white', border: 'none', borderRadius: 4, padding: '4px 8px', cursor: 'pointer' }}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table></ResponsiveTable>
    </div>
  );
}
